import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import API_BASE_URL from '../api';

const InterestsHobbiesInfo = () => {
  const [interestsHobbiesInfo, setInterestsHobbiesInfo] = useState({
    selectedInterests: [],
    lookingForSimilar: 50, // Default slider position (neutral)
  });

  const [message, setMessage] = useState('');
  const [isNewUser, setIsNewUser] = useState(false);

  const getAuthToken = () => localStorage.getItem('stack_access_token');
  const navigate = useNavigate();

  const availableInterests = [
    'Music',
    'Sports',
    'Reading',
    'Gaming',
    'Travel',
    'Cooking',
    'Photography',
    'Fitness',
    'Dancing',
    'Movies',
    'Tech',
    'Fashion',
    'Art',
    'Writing',
    'Volunteering',
  ];

  useEffect(() => {
    const token = getAuthToken();
    fetch(`${API_BASE_URL}/api/interests-hobbies`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Stack-Access-Token': token,
      },
    })
      .then((response) => {
        if (response.status === 404) {
          setIsNewUser(true);
          setMessage('No interests/hobbies info found. Please set your info.');
          return;
        }
        if (!response.ok) {
          throw new Error('Failed to fetch interests/hobbies info');
        }
        return response.json();
      })
      .then((data) => {
        console.log('Received Interests/Hobbies Data:', data); // DEBUGGING

        if (data) {
          const interestsData = {
            questionText:
              data.find(
                (item) =>
                  item.question_text ===
                  'Interests make it easy to find who shares your profile. What are some of your interests?',
              )?.question_text || '',
            selectedInterests:
              data
                .find(
                  (item) =>
                    item.question_text ===
                    'Interests make it easy to find who shares your profile. What are some of your interests?',
                )
                ?.answer_text?.split(', ') || [],
            lookingForSimilar:
              data.find(
                (item) =>
                  item.question_text ===
                  'Are you looking for someone with the same interests/hobbies?',
              )?.answer_text || '',
          };

          console.log('Parsed Interests/Hobbies Data:', interestsData); // DEBUGGING
          setInterestsHobbiesInfo(interestsData);
        }
      })
      .catch((error) => {
        console.error('Error fetching interests/hobbies info:', error);
        setMessage('Error fetching interests/hobbies info.');
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = getAuthToken();
    // Ensure we are only sending the latest interests
    const uniqueInterests = [
      ...new Set(interestsHobbiesInfo.selectedInterests),
    ];

    const payload = {
      selectedInterests: uniqueInterests,
      lookingForSimilar: interestsHobbiesInfo.lookingForSimilar,
    };

    const method = isNewUser ? 'POST' : 'PUT';

    try {
      const response = await fetch(`${API_BASE_URL}/api/interests-hobbies`, {
        method: method,
        headers: {
          'X-Stack-Access-Token': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to update interests/hobbies info');
      }

      setMessage('Interests/Hobbies info updated successfully.');
      setTimeout(() => navigate('/date-activities'), 2000);
      setIsNewUser(false);
    } catch (error) {
      console.error('Error setting interests/hobbies info:', error);
      setMessage('Failed to update interests/hobbies info.');
    }
  };

  const handleInterestSelection = (e) => {
    const values = Array.from(
      e.target.selectedOptions,
      (option) => option.value,
    );
    setInterestsHobbiesInfo({
      ...interestsHobbiesInfo,
      selectedInterests: values,
    });
  };

  const handleSliderChange = (e) => {
    setInterestsHobbiesInfo({
      ...interestsHobbiesInfo,
      lookingForSimilar: parseInt(e.target.value, 10),
    });
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#0D2329' }}
    >
      <Navbar />
      <div className="flex-1 flex justify-center items-center">
        <div
          className="w-full max-w-md p-8 rounded-lg shadow-md"
          style={{ backgroundColor: '#05664f' }}
        >
          <h1 className="text-4xl font-bold text-white text-center mb-6">
            Interests & Hobbies
          </h1>
          <p className="text-white text-center mb-4">
            Interests make it easy to find who shares your profile. Add 3-5 to
            your profile for better matches.
          </p>

          {message && (
            <p className="text-sm text-green-200 text-center mb-4">{message}</p>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Multi-select Interests Dropdown */}
            <div className="text-white">
              <label className="block text-sm font-medium mb-2">
                Select your interests:
              </label>
              <select
                multiple
                value={interestsHobbiesInfo.selectedInterests}
                onChange={handleInterestSelection}
                className="block w-full mt-1 px-3 py-2 text-base border border-gray-300 text-black focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
              >
                {availableInterests.map((interest) => (
                  <option key={interest} value={interest}>
                    {interest}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-200 mt-1">
                Hold down Ctrl (Windows) / Command (Mac) to select multiple
                options.
              </p>
            </div>

            {/* Slider for Matching Interests */}
            <div className="text-white">
              <label className="block text-sm font-medium mb-2">
                Are you looking for someone with the same interests/hobbies?
              </label>
              <input
                type="range"
                min="0"
                max="100"
                step="50"
                value={interestsHobbiesInfo.lookingForSimilar}
                onChange={handleSliderChange}
                className="w-full slider"
              />
              <div className="flex justify-between text-xs mt-1">
                <span>
                  Not really! I'm open to learning about different interests.
                </span>
                <span>Yes! I want to have these things in common.</span>
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              type="submit"
              className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Next
            </button>

            <button
              type="button"
              className="w-full px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 mt-2"
              onClick={() => navigate('/almost-done')}
            >
              Previous
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InterestsHobbiesInfo;
